"use client";

import { c as _c } from "react/compiler-runtime";
import { getMemesQuickVoteRemainingCount, type MemesQuickVoteStats } from "@/hooks/memesQuickVote.helpers";
import { createInitialMemesQuickVoteDiscoveryState, deriveMemesQuickVoteDiscoverySnapshot, getMemesQuickVoteDiscoveredQueue } from "@/hooks/memesQuickVote.queue.helpers";
import { useMemesQuickVoteContext } from "@/hooks/useMemesQuickVoteContext";
import { fetchMemesQuickVoteDiscoveryBatch } from "@/hooks/memesQuickVote.query";
import { useMemesQuickVoteSkippedDropIds } from "@/hooks/useMemesQuickVoteStorage";
import { useMemesQuickVoteSummary } from "@/hooks/useMemesQuickVoteSummary";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
type MemesWaveFooterStats = MemesQuickVoteStats & {
  readonly isReady: boolean;
};
const EMPTY_STATS: MemesWaveFooterStats = {
  uncastPower: null,
  unratedCount: 0,
  votingLabel: null,
  isReady: false
};
export const useMemesWaveFooterStats = () => {
  const $ = _c(30);
  const {
    contextProfile,
    isEnabled,
    memesWaveId,
    proxyId
  } = useMemesQuickVoteContext();
  let t0;
  if ($[0] !== contextProfile || $[1] !== memesWaveId || $[2] !== proxyId) {
    t0 = {
      contextProfile,
      memesWaveId,
      proxyId
    };
    $[0] = contextProfile;
    $[1] = memesWaveId;
    $[2] = proxyId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    setAndPersistSkippedDropIds,
    skippedDropIds
  } = useMemesQuickVoteSkippedDropIds(t0);
  const {
    isSuccess,
    stats
  } = useMemesQuickVoteSummary();
  let t1;
  if ($[4] !== skippedDropIds.length || $[5] !== stats.unratedCount) {
    t1 = getMemesQuickVoteRemainingCount({
      count: stats.unratedCount,
      hiddenCount: skippedDropIds.length
    });
    $[4] = skippedDropIds.length;
    $[5] = stats.unratedCount;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const rawUnratedCount = t1;
  const shouldRecoverVisibility = isEnabled && isSuccess && stats.unratedCount > 0 && skippedDropIds.length > 0 && rawUnratedCount <= 0 && typeof memesWaveId === "string" && memesWaveId.length > 0;
  let t2;
  if ($[7] !== skippedDropIds) {
    t2 = skippedDropIds.join(",");
    $[7] = skippedDropIds;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== contextProfile || $[10] !== memesWaveId || $[11] !== proxyId || $[12] !== t2) {
    t3 = ["memes-quick-vote-footer-recovery", {
      contextProfile,
      proxyId,
      skippedSignature: t2,
      waveId: memesWaveId
    }];
    $[9] = contextProfile;
    $[10] = memesWaveId;
    $[11] = proxyId;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== memesWaveId || $[15] !== skippedDropIds) {
    t4 = async () => {
      const waveId = memesWaveId;
      if (typeof waveId !== "string" || waveId.length === 0) {
        return 0;
      }
      const discoveryState = createInitialMemesQuickVoteDiscoveryState();
      const discoveryData = await fetchMemesQuickVoteDiscoveryBatch({
        discoveryState,
        skippedDropIds,
        waveId
      });
      const snapshot = deriveMemesQuickVoteDiscoverySnapshot({
        enabled: true,
        pages: discoveryData.pages,
        skippedDropIds,
        state: discoveryState
      });
      return getMemesQuickVoteDiscoveredQueue(snapshot).length;
    };
    $[14] = memesWaveId;
    $[15] = skippedDropIds;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== shouldRecoverVisibility || $[18] !== t3 || $[19] !== t4) {
    t5 = {
      queryKey: t3,
      enabled: shouldRecoverVisibility,
      queryFn: t4,
      retry: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: 60000
    };
    $[17] = shouldRecoverVisibility;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const recoveredFooterCount = useQuery(t5);
  let unratedCount = rawUnratedCount;
  if (shouldRecoverVisibility) {
    if (typeof recoveredFooterCount.data === "number") {
      unratedCount = recoveredFooterCount.data;
    } else {
      if (recoveredFooterCount.isError) {
        unratedCount = stats.unratedCount;
      } else {
        unratedCount = 0;
      }
    }
  }
  let t6;
  let t7;
  if ($[21] !== isSuccess || $[22] !== setAndPersistSkippedDropIds || $[23] !== skippedDropIds.length || $[24] !== stats.unratedCount) {
    t6 = () => {
      if (!isSuccess || stats.unratedCount > 0 || skippedDropIds.length === 0) {
        return;
      }
      setAndPersistSkippedDropIds(_temp);
    };
    t7 = [isSuccess, setAndPersistSkippedDropIds, skippedDropIds.length, stats.unratedCount];
    $[21] = isSuccess;
    $[22] = setAndPersistSkippedDropIds;
    $[23] = skippedDropIds.length;
    $[24] = stats.unratedCount;
    $[25] = t6;
    $[26] = t7;
  } else {
    t6 = $[25];
    t7 = $[26];
  }
  useEffect(t6, t7);
  if (shouldRecoverVisibility && recoveredFooterCount.isPending) {
    return EMPTY_STATS;
  }
  if (typeof stats.uncastPower !== "number" || unratedCount <= 0) {
    return EMPTY_STATS;
  }
  let t8;
  if ($[27] !== stats || $[28] !== unratedCount) {
    t8 = {
      ...stats,
      unratedCount,
      isReady: true
    };
    $[27] = stats;
    $[28] = unratedCount;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
};
function _temp() {
  return [];
}